/**
 * PCM conversion + resampling helpers.
 *
 * Small, dependency-free utilities shared by {@link MicCapture} and
 * {@link AudioPlayback}. The browser's `AudioContext` runs at the hardware
 * sample rate (commonly 48 kHz), while Voice Live speaks 16-bit PCM at a fixed
 * rate (commonly 24 kHz), so capture resamples context → target and playback
 * resamples target → context.
 */

/** Clamp + convert mono Float32 samples (`-1..1`) to signed 16-bit PCM. */
export function floatToPcm16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i] ?? 0));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/** Convert signed 16-bit PCM to mono Float32 samples (`-1..1`). */
export function pcm16ToFloat(input: Int16Array): Float32Array {
  const out = new Float32Array(input.length);
  for (let i = 0; i < input.length; i++) {
    out[i] = (input[i] ?? 0) / 0x8000;
  }
  return out;
}

/**
 * Linear-interpolation resample of mono Float32 audio. Fast and good enough for
 * speech; not a polyphase/anti-aliased resampler. Returns the input unchanged
 * when the rates match.
 */
export function resampleLinear(
  input: Float32Array,
  fromRate: number,
  toRate: number,
): Float32Array {
  if (fromRate === toRate || input.length === 0) return input;
  const ratio = toRate / fromRate;
  const outLen = Math.max(1, Math.round(input.length * ratio));
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const pos = i / ratio;
    const idx = Math.floor(pos);
    const frac = pos - idx;
    const a = input[idx] ?? 0;
    const b = input[idx + 1] ?? a;
    out[i] = a + (b - a) * frac;
  }
  return out;
}

/** Root-mean-square of a Float32 buffer, clamped to `0..1` — a simple level meter value. */
export function rms(buffer: Float32Array): number {
  if (buffer.length === 0) return 0;
  let sumSq = 0;
  for (let i = 0; i < buffer.length; i++) {
    const s = buffer[i] ?? 0;
    sumSq += s * s;
  }
  return Math.min(1, Math.sqrt(sumSq / buffer.length));
}
